import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import {
    Project,
    ProjectResponse,
    ProjectAccess,
    AddProjectMemberRequest,
    CreateProjectRequest,
    UpdateProjectSettingRequest, ProjectSettings, TelemetryEventsByDay, TelemetryEventsByEvent, ProjectPluginResponse
} from '../models/project';

import { ErrorService, EventService } from '..';

import { ConfigService } from './config.service';
import {
     ActivityResponse, UpdateProjectSecurityFeature, DetectionSettingUpdatePreviewRequest,
    DetectionSettingUpdatePreviewResponse, ProjectPlugin
} from '../models';
import { Match, MatchInfoResponse, MatchResponse } from '../models/match';
import { Detection } from '../models/detection';


@Injectable({ providedIn: 'root' })
export class ProjectService {
    private projectURL = ConfigService.getApiEndpoint() + 'v1/project/';

    constructor(
        private http: HttpClient,
        private eventService: EventService,
        private error: ErrorService
    ) { }

    search(query: any): Observable<ProjectResponse> {
        return this.http.get<ProjectResponse>(this.projectURL + 'search', { params: query }).pipe(
            catchError(this.error.handleError('project.search', null))
        );
    }

    getAll(): Observable<Project[]> {
        return this.http.get<Project[]>(this.projectURL).pipe(
            catchError(this.error.handleError('project.getAll', null))
        );
    }

    getOne(id: string): Observable<Project> {
        return this.http.get<Project>(this.projectURL + id).pipe(
            catchError(this.error.handleError('project.getOne', null))
        );
    }
    getOneImage(id: string): Observable<Project> {
        return this.http.get<Project>(this.projectURL + id + '/image').pipe(
            catchError(this.error.handleError('project.getOneImage', null))
        );
    }

    getPluginSettings(id: string): Observable<ProjectPluginResponse> {
        return this.http.get<ProjectPluginResponse>(this.projectURL + id + '/plugins').pipe(
            catchError(this.error.handleError('project.getPluginSettings', null))
        );
    }

    create(req: CreateProjectRequest): Observable<Project> {
        return this.http.post<Project>(this.projectURL, req).pipe(
            tap(_ => this.eventService.addAlert(`created project ${req.name}`)),
            catchError(this.error.handleError('project.create', null))
        );
    }

    update(project: Project): Observable<Project> {
        return this.http.put<Project>(this.projectURL + project.id, project).pipe(
            tap(_ => this.eventService.addAlert(`updated project: ${project.name}`)),
            catchError(this.error.handleError('project.update', null))
        );
    }

    updateSetting(project:Project, projectSetting: UpdateProjectSettingRequest): Observable<ProjectSettings> {
      return this.http.put<UpdateProjectSettingRequest>(this.projectURL + project.id + '/setting', projectSetting).pipe(
        tap(_ => this.eventService.addAlert(`updated project-setting for project: ${project.name}`)),
        catchError(this.error.handleError('project-setting.update', null))
      );
    }

    updateSettings(project:Project, projectSettings: UpdateProjectSettingRequest[]): Observable<ProjectSettings[]> {
      return this.http.put<UpdateProjectSettingRequest>(this.projectURL + project.id + '/settings', projectSettings).pipe(
        tap(_ => this.eventService.addAlert(`updated project-settings for project: ${project.name}`)),
        catchError(this.error.handleError('project-setting.update', null))
      );
    }

    delete(project: Project): Observable<Project> {
        return this.http.delete<Project>(this.projectURL + project.id).pipe(
            tap(_ => this.eventService.addAlert(`deleted project: ${project.name}`)),

            catchError(this.error.handleError('project.delete', null))
        );
    }

    getAccessUsers(id: string): Observable<ProjectAccess[]> {
        return this.http.get<ProjectAccess[]>(this.projectURL + id + '/accessUsers').pipe(
            catchError(this.error.handleError('project.getAccess', null))
        );
    }

    getAccessUserGroups(id: string): Observable<ProjectAccess[]> {
        return this.http.get<ProjectAccess[]>(this.projectURL + id + '/accessUserGroups').pipe(
            catchError(this.error.handleError('project.getAccess', null))
        );
    }

    createAccess(id: string, addProjectMemberRequest: AddProjectMemberRequest): Observable<ProjectAccess> {
        return this.http.post<ProjectAccess[]>(this.projectURL + id + '/access', addProjectMemberRequest).pipe(
            catchError(this.error.handleError('project.createAccess', null))
        );
    }

    removeAccess(id: string): Observable<ProjectAccess> {
        return this.http.delete<ProjectAccess[]>(this.projectURL + id + '/access').pipe(
            catchError(this.error.handleError('project.removeAccess', null))
        );
    }

    getUserProjectAccess(id: string): Observable<ProjectAccess> {
        return this.http.get<ProjectAccess>(this.projectURL + id + '/userProjectAccess').pipe(
            catchError(this.error.handleError('project.userProjectAccess', null))
        );
    }

    getActivities(id: string, query: any): Observable<ActivityResponse> {
        return this.http.get<ActivityResponse>(this.projectURL + id + '/activity/search', { params: query }).pipe(
            catchError(this.error.handleError('project.getActivities', null))
        );
    }

    getMatchActivities(projectId: string, matchId: string, query: any): Observable<ActivityResponse> {
        return this.http.get<ActivityResponse>(this.projectURL + projectId + '/match/' + encodeURIComponent(matchId) + '/activity/search',
          { params: query }).pipe(
            catchError(this.error.handleError('project.getActivities', null))
        );
    }

    getMatchInfo(id: string, query: any): Observable<MatchInfoResponse> {
        return this.http.get<MatchInfoResponse>(this.projectURL + id + '/match/info', { params: query }).pipe(
            catchError(this.error.handleError('project.getMatchInfo', null))
        );
    }

    getMatchList(id: string, query: any): Observable<MatchResponse> {
        return this.http.get<MatchResponse>(this.projectURL + id + '/match/list', { params: query }).pipe(
            catchError(this.error.handleError('project.getMatchList', null))
        );
    }

    getMatchByExternalId(id: string, externalMatchId: any): Observable<Match> {
        return this.http.get<Match>(this.projectURL + id + '/match', { params: {matchId: encodeURIComponent(externalMatchId)}}).pipe(
            catchError(this.error.handleError('project.getMatchByExternalId', null))
        );
    }

    updateSecurityFeature(id: string, request: UpdateProjectSecurityFeature) {
        return this.http.put<UpdateProjectSecurityFeature>(this.projectURL + id + '/securityfeature', request).pipe(
            tap(_ => this.eventService.addAlert(`updated security features`)),
            catchError(this.error.handleError('project.updateSecurityFeature', null))
        );
    }

    getDetectionSettingUpdatePreview(projectId: string, request: DetectionSettingUpdatePreviewRequest) {
        return this.http.put<DetectionSettingUpdatePreviewResponse>(this.projectURL + projectId + '/setting/updatepreview', request).pipe(
            catchError(this.error.handleError('project.getDetectionSettingUpdatePreview', null))
        );
    }

    getDetection(detectionId: string, projectId: string) {
        return this.http.get<Detection>(this.projectURL + projectId + '/detection/' + detectionId).pipe(
            catchError(this.error.handleError('project.getDetection', null))
        );
    }

    getTelemetryEventsByDay(projectId: string, startDate: string, endDate: string, query: String[]): Observable<TelemetryEventsByDay[]> {
        let httpParams = this.checkParams(startDate, endDate);
        return this.http.post<TelemetryEventsByDay[]>(this.projectURL + projectId + '/statistics/telemetry/event/all', query,{ params: httpParams }).pipe(
            catchError(this.error.handleError('project.getTelemetryEventsByDay', null))
        );
    }

    getTelemetryEventsByEventByDay(projectId: string, event: string, startDate: string, endDate: string, query: String[]): Observable<TelemetryEventsByDay[]> {
        let httpParams = this.checkParams(startDate, endDate);
        return this.http.post<TelemetryEventsByDay[]>(this.projectURL + projectId + '/statistics/telemetry/event/' + event, query,{ params: httpParams }).pipe(
            catchError(this.error.handleError('project.getTelemetryEventsByEventByDay', null))
        );
    }

    getTelemetryEventsByEvent(projectId: string, startDate: string, endDate: string, query: String[]): Observable<TelemetryEventsByEvent[]> {
        let httpParams = this.checkParams(startDate, endDate);
        return this.http.post<TelemetryEventsByEvent[]>(this.projectURL + projectId + '/statistics/telemetry/event', query,{ params: httpParams }).pipe(
            catchError(this.error.handleError('project.getTelemetryEventsByEvent', null))
        );
    }

    getTelemetryEventsByDayForTransaction(projectId: string, transactionId: string, startDate: string, endDate: string): Observable<TelemetryEventsByDay[]> {
        let httpParams = this.checkParams(startDate, endDate);
        return this.http.get<TelemetryEventsByDay[]>(this.projectURL + projectId + '/transaction/' + transactionId + '/statistics/telemetry/event/all', { params: httpParams }).pipe(
            catchError(this.error.handleError('project.getTelemetryEventsByDayForTransaction', null))
        );
    }

    getTelemetryEventsByEventByDayForTransaction(projectId: string, transactionId: string, event: string, startDate: string, endDate: string): Observable<TelemetryEventsByDay[]> {
        let httpParams = this.checkParams(startDate, endDate);
        return this.http.get<TelemetryEventsByDay[]>(this.projectURL + projectId + '/transaction/' + transactionId + '/statistics/telemetry/event/' + event, { params: httpParams }).pipe(
            catchError(this.error.handleError('project.getTelemetryEventsByEventByDayForTransaction', null))
        );
    }

    getTelemetryEventsByEventForTransaction(projectId: string, transactionId: string, startDate: string, endDate: string): Observable<TelemetryEventsByEvent[]> {
        let httpParams = this.checkParams(startDate, endDate);
        return this.http.get<TelemetryEventsByEvent[]>(this.projectURL + projectId + '/transaction/' + transactionId + '/statistics/telemetry/event', { params: httpParams }).pipe(
            catchError(this.error.handleError('project.getTelemetryEventsByEventForTransaction', null))
        );
    }

    checkParams(startDate: string, endDate: string):HttpParams {
        let httpParams = new HttpParams();
        if(startDate != null && endDate != null){
            httpParams = new HttpParams({ fromObject: { startDate: startDate, endDate: endDate } });
        }
        return httpParams;
    }

}
